import { useEffect, useState } from "react";
import API from "@src/API.js";
import CustomButton from "../Controls/CustomButton";
import { toast } from "react-toastify";

export default function CxSurveyPanel() {
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState(null);
  const [rating, setRating] = useState(5);
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const resp = await API.getAsync("/CxSurvey/Pending");
    if (resp?.ok) setPending(resp.jsonData || []);
  }

  useEffect(() => {
    load();
  }, []);

  function openSurvey(s) {
    setActive(s);
    setRating(5);
    setComment("");
    const initial = {};
    (s.Questions || s.questions || []).forEach((q) => {
      initial[q] = "";
    });
    setAnswers(initial);
  }

  async function submit(declined) {
    if (!active || busy) return;
    setBusy(true);
    try {
      const payload = {
        SurveyType: active.SurveyType ?? active.surveyType,
        JobCandidateID: active.JobCandidateID ?? active.jobCandidateID ?? null,
        Declined: !!declined,
        Rating: declined ? null : Number(rating),
        Answers: declined ? null : answers,
        Comment: declined ? null : comment || null,
      };
      const resp = await API.postAsync("/CxSurvey", payload);
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Survey save failed");
        return;
      }
      toast.success(declined ? "Survey skipped" : "Thanks for your feedback");
      setActive(null);
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (!pending.length && !active) return null;

  return (
    <div className="tabsection" data-testid="cx-survey-panel">
      <div className="listTitle">
        <span>Surveys</span>
      </div>
      {!active ? (
        <ul data-testid="cx-survey-pending-list">
          {pending.map((s, i) => {
            const type = s.SurveyType ?? s.surveyType;
            const jc = s.JobCandidateID ?? s.jobCandidateID;
            return (
              <li key={`${type}-${jc || 0}-${i}`}>
                <CustomButton type="button" variant="contained" color="primary" data-testid={`cx-survey-open-${type}`} onClick={() => openSurvey(s)}>
                  {s.Name || s.name}
                  {jc ? ` (interview #${jc})` : ""}
                </CustomButton>
              </li>
            );
          })}
        </ul>
      ) : (
        <form
          className="cxSurveyForm"
          data-testid="cx-survey-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit(false);
          }}
        >
          <h3>{active.Name || active.name}</h3>
          <p className="surveyPurpose" data-testid="cx-survey-purpose">
            {active.Purpose || active.purpose}
          </p>
          <label>
            Overall rating (1–5)
            <select
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              data-testid="cx-survey-rating"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {(active.Questions || active.questions || []).map((q) => (
            <label key={q}>
              {q}
              <input
                value={answers[q] || ""}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q]: e.target.value }))}
                data-testid="cx-survey-answer"
              />
            </label>
          ))}
          <label>
            Additional comments
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              data-testid="cx-survey-comment"
            />
          </label>
          <div className="formActions">
            <CustomButton type="submit" variant="contained" color="primary" disabled={busy} data-testid="cx-survey-submit">
              Submit
            </CustomButton>
            <CustomButton type="button" variant="outlined" color="secondary" disabled={busy} data-testid="cx-survey-decline" onClick={() => submit(true)}>
              Skip
            </CustomButton>
          </div>
        </form>
      )}
    </div>
  );
}
